import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BookOpen, ChevronDown, Loader2, Download } from 'lucide-react';
import PageHero from '../components/PageHero';
import { useTranslation } from '../i18nContext';
import { api, absoluteUrl } from '../lib/api';
import MembershipCards from '../components/MembershipCards';
import { normalizeHtml, looksLikeHtml } from '../lib/htmlText';

function LegalText({ content, className }) {
  if (looksLikeHtml(content)) {
    return (
      <div
        className={`prose prose-sm max-w-none prose-p:my-2 prose-headings:text-charcoal-700 prose-strong:text-charcoal-700 prose-a:text-cmic-600 break-words ${className || ''}`}
        dangerouslySetInnerHTML={{ __html: normalizeHtml(content) }}
      />
    );
  }
  return <p className={`whitespace-pre-line ${className || ''}`}>{content}</p>;
}

const SLUG_TO_KEY = {
  admisiones:  'reg-admisiones',
  tesoreria:   'reg-tesoreria',
  etica:       'reg-etica',
  redes:       'reg-redes',
  malintzin:   'reg-malintzin',
  solidaridad: 'reg-solidaridad',
};

export default function RegulationPage() {
  const { slug } = useParams();
  const t = useTranslation();
  const locale = t.locale;
  const [regulation, setRegulation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const docKey = SLUG_TO_KEY[slug];
    if (!docKey) { setRegulation(null); setLoading(false); return; }
    setLoading(true);
    api.get(`/legal/${docKey}`)
      .then((r) => setRegulation(r.data))
      .catch(() => setRegulation(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <section className="py-32 flex items-center justify-center"><Loader2 className="animate-spin text-cmic-500" size={32} /></section>
    );
  }
  if (!regulation) {
    return (
      <>
        <PageHero title={locale === 'es' ? 'Reglamento no encontrado' : 'Regulation not found'} />
        <section className="py-20 text-center">
          <p className="text-charcoal-500">
            {locale === 'es' ? 'El reglamento solicitado no existe.' : 'The requested regulation does not exist.'}
          </p>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero label={locale === 'es' ? 'Reglamentos CMIC' : 'CMIC Regulations'} title={regulation.title?.[locale] || regulation.title?.es || ''} />
      <section className="py-16 sm:py-20 bg-white" data-testid={`regulation-${slug}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-10">
            <div className="w-12 h-12 rounded-xl bg-cmic-100 flex items-center justify-center flex-shrink-0">
              <BookOpen size={22} className="text-cmic-600" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <h2 className="text-xl sm:text-2xl font-bold text-charcoal-800">{regulation.title?.[locale] || regulation.title?.es || ''}</h2>
              <p className="text-charcoal-400 text-sm mt-1">
                {locale === 'es' ? `${regulation.chapters.length} capítulos` : `${regulation.chapters.length} chapters`}
              </p>
            </div>
            {regulation.show_download !== false && regulation.pdf_url && (
              <a
                href={absoluteUrl(regulation.pdf_url)}
                download
                data-testid="regulation-download-pdf"
                className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white px-5 py-2.5 rounded-xl font-semibold shadow-sm hover:shadow-md transition-all text-sm"
              >
                <Download size={16} />
                {regulation.download_label?.[locale] || regulation.download_label?.es || (locale === 'es' ? 'Descargar PDF' : 'Download PDF')}
              </a>
            )}
          </div>
          <div className="space-y-3">
            {regulation.chapters.map((chapter, ci) => {
              const isAdmissionsChapterII = slug === 'admisiones' && ci === 1;
              const customContent = isAdmissionsChapterII ? <MembershipCards locale={locale} /> : null;
              return (
                <ChapterAccordion key={ci} chapter={chapter} locale={locale} defaultOpen={ci === 0} customContent={customContent} />
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}

function ChapterAccordion({ chapter, locale, defaultOpen, customContent }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-charcoal-100 rounded-xl overflow-hidden transition-all duration-200 hover:border-cmic-200">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 text-left bg-charcoal-50/50 hover:bg-cmic-50/50 transition-colors">
        <span className="font-semibold text-charcoal-700 text-sm sm:text-base pr-4">{chapter.title[locale]}</span>
        <ChevronDown size={18} className={`text-charcoal-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 py-5 border-t border-charcoal-100 bg-white">
          {customContent || (
            <div className="space-y-5">
              {chapter.articles.map((article, ai) => (
                <div key={ai} className="pl-4 border-l-2 border-cmic-200">
                  {article.number && (
                    <span className="inline-block text-xs font-bold text-cmic-600 bg-cmic-50 rounded px-2 py-0.5 mb-2">
                      {locale === 'es' ? 'Artículo' : 'Article'} {article.number}
                    </span>
                  )}
                  <LegalText content={article[locale] || article.es} className="text-charcoal-600 text-sm leading-relaxed" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
